import { FC, useCallback, useState, useRef, useEffect, useMemo } from "react";
import {
  Document as ReactPdfDocument,
  Page,
  pdfjs,
  DocumentProps,
} from "react-pdf";
import { useWindowSize } from "@uidotdev/usehooks";
import { initiateDownload } from "../../utils/download-utils";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "./PingPdfPreview.scss";
import { Button } from "@/index";

// Set worker source for react-pdf
// Vite will automatically bundle this as an asset with cache-busting hash
// Falls back to CDN if import fails (e.g., non-Vite environments)
if (typeof window !== "undefined") {
  try {
    // Use dynamic import to avoid breaking non-Vite builds
    // The ?url suffix tells Vite to emit this as an asset and return its URL
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      "pdfjs-dist/build/pdf.worker.min.mjs",
      import.meta.url,
    ).href;
  } catch {
    // Fallback to CDN if local bundling isn't available
    pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  }
}

const getDocumentLabel = (fileExtension?: string | null): string => {
  const upper = fileExtension?.toUpperCase();
  if (upper === "XLS") return "XLS";
  if (upper === "XLSX") return "XLSX";
  if (upper === "PDF") return "PDF";
  return "document";
};

const CustomLoading = ({
  fileExtension,
}: {
  fileExtension?: string | null;
}) => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      textAlign: "center",
    }}
  >
    <div>
      <h3>Loading your {getDocumentLabel(fileExtension)} file...</h3>
      <p>Please wait a moment</p>
    </div>
  </div>
);

const CustomError = ({ fileExtension }: { fileExtension?: string | null }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
      textAlign: "center",
    }}
  >
    <div>
      <h3>Failed to load {getDocumentLabel(fileExtension)} file.</h3>
      <p>The file could not be displayed. Please try downloading it instead.</p>
    </div>
  </div>
);

type PDFPreviewProps = {
  downloadUrl: string;
  fileName: string;
  accessToken?: string | null;
  documentOptions?: DocumentProps["options"];
  fileExtension?: string | null;
  onClose?(): void;
  onEditClick?: () => void;
  highlightPage?: number;
  highlightCoords?: {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
  }[];
  showControls?: boolean;
  onPageChange?: (details: { currentPage: number; totalPages: number }) => void;
};

const LARGE_SCREEN_WIDTH_THRESHOLD = 2500;
const PDF_NORMAL_SCREEN_WIDTH = 750;
const PDF_LARGE_SCREEN_WIDTH = 1000;

export const PingPdfPreview: FC<PDFPreviewProps> = ({
  downloadUrl,
  fileName,
  accessToken,
  documentOptions,
  fileExtension,
  onClose,
  onEditClick,
  highlightPage,
  highlightCoords: initialHighlightCoords,
  showControls = true,
  onPageChange,
}) => {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const windowSize = useWindowSize();

  const scrollToHighlightedPage = useCallback(() => {
    if (highlightPage && pageRefs.current[highlightPage - 1]) {
      pageRefs.current[highlightPage - 1]?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [pageRefs, highlightPage]);

  useEffect(() => {
    if (highlightPage) {
      setCurrentPage(highlightPage);
      const timer = setTimeout(() => {
        scrollToHighlightedPage();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [highlightPage, scrollToHighlightedPage]);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (canvasRef.current) {
        const { width, height } = canvasRef.current.getBoundingClientRect();
        setCanvasSize({ width, height });
      }
    };

    const resizeObserver = new ResizeObserver(updateCanvasSize);
    if (canvasRef.current) {
      resizeObserver.observe(canvasRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [dimensions]);

  const onPageLoadSuccess = useCallback((page: any) => {
    const { width, height } = page.getViewport({ scale: 1 });
    setDimensions({ width, height });
  }, []);

  const pixelToBrowser = useCallback(
    (x: number, y: number) => {
      return {
        x: (canvasSize.width * (72 * (x / 300))) / dimensions.width,
        y: (canvasSize.height * (72 * (y / 300))) / dimensions.height,
      };
    },
    [dimensions, canvasSize.width, canvasSize.height],
  );

  const coords = useMemo(() => {
    return initialHighlightCoords?.map((coord) => {
      const { x, y } = pixelToBrowser(coord.x1, coord.y1);
      const { x: x2, y: y2 } = pixelToBrowser(coord.x2, coord.y2);
      const w = x2 - x;
      const h = y2 - y;

      return {
        x: x,
        y: y,
        width: w,
        height: h,
      };
    });
  }, [initialHighlightCoords, pixelToBrowser]);

  useEffect(() => {
    if (numPages === null) return;
    onPageChange?.({ currentPage, totalPages: numPages });
  }, [currentPage, numPages, onPageChange]);

  const handleDownload = useCallback(() => {
    initiateDownload(downloadUrl, accessToken || "", fileName);
  }, [downloadUrl, fileName, accessToken]);

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
      pageRefs.current = new Array(numPages).fill(null);
    },
    [],
  );

  const handleScroll = useCallback(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;
    const scrollTop = container.scrollTop;
    const clientHeight = container.clientHeight;
    const scrollPosition = scrollTop + clientHeight / 2;
    pageRefs.current.forEach((pageRef, index) => {
      if (!pageRef) return;
      const pageTop = pageRef.offsetTop;
      const pageBottom = pageTop + pageRef.offsetHeight;
      if (scrollPosition >= pageTop && scrollPosition <= pageBottom) {
        setCurrentPage(index + 1);
      }
    });
  }, []);

  const HighlightBox = useCallback(() => {
    if (!coords?.length || currentPage !== highlightPage) return null;

    return coords.map((highlightCoords, index) => (
      <div
        key={`highlight-${index}`}
        style={{
          position: "absolute",
          left: `${highlightCoords.x}px`,
          top: `${highlightCoords.y}px`,
          width: `${highlightCoords.width}px`,
          height: `${highlightCoords.height}px`,
          backgroundColor: "rgba(244, 8, 8, 0.7)",
          pointerEvents: "none",
          zIndex: 1000,
        }}
      />
    ));
  }, [coords, currentPage, highlightPage]);

  return (
    <div className="PingPdfPreview">
      {showControls && (
        <div className="PingPdfPreview__Controls">
          {onClose && (
            <Button iconName="X" variant="outline" onClick={onClose} />
          )}
          <div className="PingPdfPreview__Controls__PageInfo">
            {numPages && `Page ${currentPage} of ${numPages}`}
          </div>

          <div className="PingPdfPreview__Controls__FileOptions">
            <Button
              onClick={onEditClick}
              className="PingPdfPreview__Controls__EditButton"
              label="Update"
              tooltip="Update"
              tooltipPlacement="top"
              iconName="Edit"
              variant="outline"
            />
            <Button
              className="PingPdfPreview__Controls__DownloadButton"
              tooltip="Download File"
              iconName="Download"
              label="Download"
              tooltipPlacement="top"
              variant="outline"
              onClick={handleDownload}
            />
          </div>
        </div>
      )}

      <div
        className="PingPdfPreview__PdfContainer"
        ref={containerRef}
        onScroll={handleScroll}
        style={{ position: "relative" }}
      >
        <ReactPdfDocument
          loading={<CustomLoading fileExtension={fileExtension} />}
          error={<CustomError fileExtension={fileExtension} />}
          file={downloadUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          className="PingPdfPreview__PdfContainer__PdfDocument"
          options={documentOptions}
        >
          {numPages !== null &&
            Array.from({ length: numPages }, (_, index) => (
              <div
                key={`page_${index + 1}`}
                ref={(el) => {
                  if (el) pageRefs.current[index] = el;
                }}
                style={{ position: "relative" }}
              >
                <Page
                  canvasRef={canvasRef}
                  pageNumber={index + 1}
                  onLoadSuccess={onPageLoadSuccess}
                  width={
                    (windowSize.width ?? 0) >= LARGE_SCREEN_WIDTH_THRESHOLD
                      ? PDF_LARGE_SCREEN_WIDTH
                      : PDF_NORMAL_SCREEN_WIDTH
                  }
                  className="PingPdfPreview__PdfContainer__Page"
                />
                {index + 1 === highlightPage && <HighlightBox />}
              </div>
            ))}
        </ReactPdfDocument>
      </div>
    </div>
  );
};
